import type { TreeType } from "../contexts/TreeContext";
import { siteHelpBySlug } from "./siteHelpContent";

export interface HelpContent {
  title: string;
  body: string; // HTML string
  loading?: boolean;
  error?: string;
  slug?: string;
  guideHref?: string;
  source?: "site";
}

// Map React routes to Git-authored site help slugs.
const ROUTE_TO_SLUG_MAP: Record<string, string> = {
  "/": "app-home-route",
  "/home": "app-home-route",
  "/devices": "app-devices-route",
  "/events": "app-events-route",
  "/events/new": "app-new-event-route",
  "/venues": "app-venues-route",
  "/venues/new": "app-new-venue-route",
  "/agent": "app-agents-route",
  "/agents": "app-agents-route",
  "/agent-log": "app-agent-log-route",
  "/people": "app-people-profile-route",
  "/edit-profile": "app-people-profile-route",
  "/readings": "app-readings-route",
  "/report": "app-report-route",
  "/category-engineering": "app-category-engineering-route",
  "/product-engineering": "app-product-engineering-route",
  "/device-engineering": "app-device-engineering-route",
  "/device-report": "app-device-report-route",
  "/category-report": "app-category-report-route",
  "/product-report": "app-product-report-route",
  "/zone-report": "app-zone-report-route",
  "/event-report": "app-event-report-route",
  "/venue-report": "app-venue-report-route",
  "/event-map": "app-event-map-route",
  "/venue-map": "app-venue-map-route",
  "/zone-map": "app-zone-map-route",
  "/theme-info": "app-theme-info-route",
};

// Map tree types to Git-authored site help slugs.
const TREE_TYPE_TO_SLUG: Record<TreeType, string> = {
  devices: "devices-tree",
  events: "events-tree",
  venues: "venues-tree",
  "single-event": "single-event-tree",
  agents: "app-agents-route",
  "unassigned-devices": "devices-tree",
};

/**
 * Get help content for a given pathname.
 * Uses Git-authored help exported from the site content collection.
 * Returns a Promise that resolves to HelpContent.
 */
export const getHelpContent = async (
  pathname: string
): Promise<HelpContent> => {
  // Extract base route (e.g., "/device-report/7" -> "/device-report")
  const pathParts = pathname.split("/").filter(Boolean);
  const baseRoute = pathParts.length > 0 ? `/${pathParts[0]}` : "/";

  // Get the Git-authored help slug for this route.
  const slug =
    ROUTE_TO_SLUG_MAP[pathname] || ROUTE_TO_SLUG_MAP[baseRoute] || "default";
  const siteHelpEntry = siteHelpBySlug[slug];

  if (siteHelpEntry) {
    return {
      title: siteHelpEntry.title,
      body: siteHelpEntry.body,
      guideHref: siteHelpEntry.guideHref,
      slug,
      source: "site",
    };
  }

  return {
    title: "Help",
    body: "<p>Help content has not been written for this topic yet.</p>",
    error: `Missing Git-authored help entry for slug: ${slug}`,
    slug,
  };
};

/**
 * Get help content for a given tree type.
 * Uses Git-authored help exported from the site content collection.
 * Returns a Promise that resolves to HelpContent.
 */
export const getHelpContentForTree = async (
  treeType: TreeType
): Promise<HelpContent> => {
  // Get the Git-authored help slug for this tree type.
  const slug = TREE_TYPE_TO_SLUG[treeType] || "default";
  const siteHelpEntry = siteHelpBySlug[slug];

  if (siteHelpEntry) {
    return {
      title: siteHelpEntry.title,
      body: siteHelpEntry.body,
      guideHref: siteHelpEntry.guideHref,
      slug,
      source: "site",
    };
  }

  return {
    title: "Help",
    body: "<p>Help content has not been written for this tree yet.</p>",
    error: `Missing Git-authored tree help entry for slug: ${slug}`,
    slug,
  };
};
